import { provName, toProv } from "../components/Analytics/PriceReports/provinceUtils";
import { BEEF_AI_ACCESS_TOKEN, BEEF_AI_BASE_URL, beefAiGet, beefAiPatch } from "../common/utils/beefAiClient";
import {
  getAvgFromBeefAiAggregateRow,
  getCountFromBeefAiAggregateRow,
  pickLowestAndHighestProvinceByAvgPrice,
} from "../routes/pricing-reports/productSnapshotChartUtils";
import {
  lookbackDateBounds,
  QUARTERLY_LOOKBACK_MAX_YEARS,
  QUARTERLY_LOOKBACK_YEAR_OPTIONS,
} from "./pricingLookback";

export { lookbackDateBounds, QUARTERLY_LOOKBACK_MAX_YEARS, QUARTERLY_LOOKBACK_YEAR_OPTIONS };

export function beefAiConfigured() {
  return Boolean(BEEF_AI_BASE_URL && BEEF_AI_ACCESS_TOKEN);
}

function accessToken() {
  return BEEF_AI_ACCESS_TOKEN;
}

function buildListQuery(baseParams) {
  const params = new URLSearchParams();
  Object.entries(baseParams).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    params.set(k, String(v));
  });
  return params.toString();
}

export function applyPriceHistoryQualityFilters(params, filters = {}) {
  if (Array.isArray(filters.providersInclude) && filters.providersInclude.length > 0) {
    params.set("filter[provider][_in]", filters.providersInclude.join(","));
  }
  if (Array.isArray(filters.providersExclude) && filters.providersExclude.length > 0) {
    params.set("filter[provider][_nin]", filters.providersExclude.join(","));
  }
  params.set("filter[unit_price][_nnull]", "true");
  params.set("filter[unit_price][_gt]", "0");
  params.set("filter[price][_nnull]", "true");
  params.set("filter[price][_gt]", "0");
  params.set("filter[province][_nnull]", "true");
  params.set("filter[province][_nempty]", "true");
  params.set("filter[retailer][_nnull]", "true");
  params.set("filter[retailer][_nempty]", "true");
  params.set("filter[product_id][_nnull]", "true");
  params.set("filter[unit_type][_nnull]", "true");
  params.set("filter[unit_type][_nempty]", "true");
  params.set("filter[unit][_nnull]", "true");
  params.set("filter[unit][_nempty]", "true");
  if (filters.unitType) params.set("filter[unit_type][_eq]", filters.unitType);
  if (filters.unit) params.set("filter[unit][_eq]", filters.unit);
  if (filters.voidedOnly) {
    params.set("filter[is_void][_eq]", "true");
  } else {
    params.set("filter[_and][0][_or][0][is_void][_null]", "true");
    params.set("filter[_and][0][_or][1][is_void][_eq]", "false");
  }
}

export function applyPriceHistoryDimensionFilters(params, filters = {}, options = {}) {
  const { includeDate = true } = options;
  if (includeDate && filters.dateFrom) params.set("filter[record_at][_gte]", filters.dateFrom);
  if (includeDate && filters.dateTo) params.set("filter[record_at][_lte]", filters.dateTo);
  if (filters.province) params.set("filter[province][_eq]", filters.province);
  if (Array.isArray(filters.retailers) && filters.retailers.length > 0) {
    params.set("filter[retailer][_in]", filters.retailers.join(","));
  } else if (filters.retailer) {
    params.set("filter[retailer][_eq]", filters.retailer);
  }
  if (Array.isArray(filters.productIds) && filters.productIds.length > 0) {
    params.set("filter[product_id][_in]", filters.productIds.join(","));
  } else if (filters.productId) {
    params.set("filter[product_id][_eq]", filters.productId);
  }
  if (filters.provider) params.set("filter[provider][_eq]", filters.provider);

  // Discount exclusion is temporarily off. Regular and discounted rows both stay in the query.
  // Restore by uncommenting:
  // if (filters.isDiscounted === true) {
  //   params.set("filter[is_discounted][_eq]", "true");
  // } else if (filters.isDiscounted === false) {
  //   params.set("filter[_and][1][_or][0][is_discounted][_null]", "true");
  //   params.set("filter[_and][1][_or][1][is_discounted][_eq]", "false");
  // }
  // When `isDiscounted` is omitted (e.g. Product Snapshot), do not filter on discount.
}

export function createChartAggregateParams(filters = {}, options = {}) {
  const {
    groupBy = [],
    sort = "year(record_at),month(record_at)",
    includeDate = true,
    aggregateField = "unit_price",
    includeMinMax = false,
  } = options;
  const params = new URLSearchParams();
  params.set("access_token", accessToken());
  params.set("aggregate[avg]", aggregateField);
  params.set("aggregate[count]", "*");
  if (includeMinMax) {
    params.set("aggregate[min]", aggregateField);
    params.set("aggregate[max]", aggregateField);
  }
  groupBy.forEach((key) => params.append("groupBy[]", key));
  params.set("limit", "-1");
  if (sort) params.set("sort", sort);
  applyPriceHistoryQualityFilters(params, filters);
  applyPriceHistoryDimensionFilters(params, filters, { includeDate });
  return params;
}

export function normalizePriceHistoryImages(imagesValue) {
  let raw = imagesValue;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch (_) {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (typeof entry === "string") return entry;
      if (entry && typeof entry === "object" && typeof entry.url === "string") return entry.url;
      return "";
    })
    .filter(Boolean);
}

function normalizeRevisionMetadata(metadataValue) {
  if (!metadataValue) return null;
  if (typeof metadataValue === "string") {
    try {
      const parsed = JSON.parse(metadataValue);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (_) {
      return null;
    }
  }
  return typeof metadataValue === "object" ? metadataValue : null;
}

export const PRICE_HISTORY_BREAKDOWN_GPS_PRESENT_JSON_FIELD =
  "json(metadata, exif_resolution.gps_present)";
export const PRICE_HISTORY_BREAKDOWN_RAW_GPS_JSON_FIELD = "json(metadata, exif_resolution.raw_gps)";

export const PRICE_HISTORY_BREAKDOWN_GPS_PRESENT_JSON_ALIAS =
  "metadata_exif_resolution_gps_present_json";
export const PRICE_HISTORY_BREAKDOWN_RAW_GPS_JSON_ALIAS = "metadata_exif_resolution_raw_gps_json";

function normalizeJsonFieldValue(value) {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : parsed ?? null;
    } catch (_) {
      return null;
    }
  }
  return value;
}

function normalizeGpsPresentValue(value) {
  if (value === true || value === 1) return true;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1";
  }
  return false;
}

export function normalizeExifResolutionGpsFromBreakdownRow(row) {
  const gpsPresent = row?.[PRICE_HISTORY_BREAKDOWN_GPS_PRESENT_JSON_ALIAS];
  const rawGps = normalizeJsonFieldValue(row?.[PRICE_HISTORY_BREAKDOWN_RAW_GPS_JSON_ALIAS]);

  return {
    gps_present: normalizeGpsPresentValue(gpsPresent),
    raw_gps:
      rawGps && typeof rawGps === "object"
        ? {
            latitude: rawGps.latitude,
            longitude: rawGps.longitude,
          }
        : null,
  };
}

export function normalizePriceHistoryBreakdownRow(row) {
  const {
    [PRICE_HISTORY_BREAKDOWN_GPS_PRESENT_JSON_ALIAS]: _gpsPresentAlias,
    [PRICE_HISTORY_BREAKDOWN_RAW_GPS_JSON_ALIAS]: _rawGpsAlias,
    ...rest
  } = row || {};

  return {
    ...rest,
    images: normalizePriceHistoryImages(row?.images),
    human_verified: row?.human_verified === 1 || row?.human_verified === true,
    is_void: row?.is_void === 1 || row?.is_void === true,
    revision_status: row?.revision_status || null,
    revision_metadata: normalizeRevisionMetadata(row?.revision_metadata),
    exifResolutionGps: normalizeExifResolutionGpsFromBreakdownRow(row),
  };
}

function monthBounds(year, month) {
  if (!year || !month) return null;
  const normalizedMonth = String(month).padStart(2, "0");
  const start = `${year}-${normalizedMonth}-01`;
  const endDate = new Date(Date.UTC(Number(year), Number(month), 0));
  const end = `${endDate.toISOString().slice(0, 10)}T23:59:59.999Z`;
  return { start, end };
}

/**
 * Applies the same `price_history` filter slice as breakdown / cover fetches:
 * quality filters, panel dimension filters (including dates), month bounds, and per-cell breakdown equality.
 *
 * @param {URLSearchParams} params
 * @param {Parameters<typeof fetchPriceHistoryBreakdown>[0]} filters
 * @param {Parameters<typeof fetchPriceHistoryBreakdown>[1]} breakdown
 */
function applyPriceHistoryBreakdownSliceFilters(params, filters, breakdown) {
  applyPriceHistoryQualityFilters(params, filters);
  applyPriceHistoryDimensionFilters(params, filters, { includeDate: true });

  const bounds = monthBounds(breakdown?.year, breakdown?.month);
  if (bounds) {
    params.set("filter[record_at][_gte]", bounds.start);
    params.set("filter[record_at][_lte]", bounds.end);
  }
  if (breakdown?.province) params.set("filter[province][_eq]", breakdown.province);
  if (breakdown?.retailer) params.set("filter[retailer][_eq]", breakdown.retailer);
  if (breakdown?.productId) params.set("filter[product_id][_eq]", breakdown.productId);
  if (breakdown?.provider) params.set("filter[provider][_eq]", breakdown.provider);
  if (breakdown?.unitType) params.set("filter[unit_type][_eq]", breakdown.unitType);
  if (breakdown?.unit) params.set("filter[unit][_eq]", breakdown.unit);
}

/**
 * Stable cache key for one aggregate group cell (cover map, SWR, etc.).
 *
 * @param {Parameters<typeof fetchPriceHistoryBreakdown>[1]} breakdown
 */
export function buildAggregateGroupRowKey(breakdown) {
  const parts = [
    breakdown?.year ?? "",
    breakdown?.month ?? "",
    breakdown?.province ?? "",
    breakdown?.retailer ?? "",
    breakdown?.productId ?? "",
    breakdown?.provider ?? "",
    breakdown?.unitType ?? "",
    breakdown?.unit ?? "",
  ];
  return parts.join("|");
}

const AGGREGATE_COVER_FETCH_CONCURRENCY = 8;

async function runTasksWithConcurrency(tasks, concurrency) {
  if (!tasks.length) return [];
  const results = new Array(tasks.length);
  let nextTaskIndex = 0;

  async function worker() {
    while (nextTaskIndex < tasks.length) {
      const taskIndex = nextTaskIndex;
      nextTaskIndex += 1;
      results[taskIndex] = await tasks[taskIndex]();
    }
  }

  const workerCount = Math.min(concurrency, tasks.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

/** Default grouped-row order for pricing report aggregate tables. */
export const DEFAULT_PRICE_HISTORY_AGGREGATE_SORT =
  "-year(record_at),-month(record_at),province,retailer,product_id";

/** Directus accepts multiple aggregates per function; use append (set overwrites duplicates). */
function appendPriceHistoryFieldAggregates(params, fieldName) {
  params.append("aggregate[avg]", fieldName);
  params.append("aggregate[min]", fieldName);
  params.append("aggregate[max]", fieldName);
}

function buildPriceHistoryAggregateQueryParams(
  filters,
  { page = 1, limit = 25, sort = DEFAULT_PRICE_HISTORY_AGGREGATE_SORT } = {}
) {
  const params = new URLSearchParams();
  params.set("access_token", accessToken());
  appendPriceHistoryFieldAggregates(params, "unit_price");
  appendPriceHistoryFieldAggregates(params, "unit_quantity");
  appendPriceHistoryFieldAggregates(params, "raw_unit_quantity");
  params.set("aggregate[count]", "*");
  params.append("groupBy[]", "year(record_at)");
  params.append("groupBy[]", "month(record_at)");
  params.append("groupBy[]", "province");
  params.append("groupBy[]", "retailer");
  params.append("groupBy[]", "product_id");
  params.append("groupBy[]", "provider");
  params.append("groupBy[]", "unit_type");
  params.append("groupBy[]", "unit");
  params.set("limit", String(limit));
  params.set("page", String(page));
  params.set("sort", sort);
  applyPriceHistoryQualityFilters(params, filters);
  applyPriceHistoryDimensionFilters(params, filters, { includeDate: true });
  return params;
}

/**
 * Server-side aggregates: avg unit_price and count per calendar month × province × retailer × product.
 * Paginated via Directus `page` / `limit` over **grouped** rows.
 *
 * Do not use `meta.filter_count` for pagination totals on this endpoint — it counts underlying
 * `price_history` rows, not aggregate groups. Use {@link fetchPriceHistoryAggregateGroupCount}.
 *
 * @param {{
 *  dateFrom?: string,
 *  dateTo?: string,
 *  province?: string,
 *  retailer?: string,
 *  productId?: string,
 *  providersInclude?: string[],
 *  providersExclude?: string[],
 *  unitType?: string
 * }} filters
 * @param {{ page?: number, limit?: number, sort?: string }} [pagination]
 * `page` is 1-based (Directus). Defaults: page 1, limit 25.
 * `sort` uses Directus aggregate aliases such as `-avg.unit_price` (not `avg(unit_price)`).
 * @returns {Promise<{ data: any[] }>}
 */
export async function fetchPriceHistoryAggregates(filters, pagination = {}) {
  const limit = pagination.limit != null ? pagination.limit : 25;
  const page = pagination.page != null ? pagination.page : 1;
  const sort = pagination.sort != null ? pagination.sort : DEFAULT_PRICE_HISTORY_AGGREGATE_SORT;
  const params = buildPriceHistoryAggregateQueryParams(filters, { page, limit, sort });
  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  return { data: json.data || [] };
}

/**
 * Count aggregate **groups** for current filters (one row per month × province × retailer × …).
 *
 * @param {{
 *  dateFrom?: string,
 *  dateTo?: string,
 *  province?: string,
 *  retailer?: string,
 *  productId?: string,
 *  providersInclude?: string[],
 *  providersExclude?: string[],
 *  unitType?: string
 * }} filters
 * @returns {Promise<number>}
 */
export async function fetchPriceHistoryAggregateGroupCount(filters = {}) {
  const params = buildPriceHistoryAggregateQueryParams(filters, { page: 1, limit: -1 });
  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  return (json.data || []).length;
}

/**
 * Fetch all grouped aggregate rows for current filters by paging until a short page is returned.
 *
 * @param {{
 *  dateFrom?: string,
 *  dateTo?: string,
 *  province?: string,
 *  retailer?: string,
 *  productId?: string,
 *  providersInclude?: string[],
 *  providersExclude?: string[],
 *  unitType?: string
 * }} filters
 * @param {{ limit?: number, maxPages?: number }} [options]
 * @returns {Promise<any[]>}
 */
export async function fetchAllPriceHistoryAggregates(filters, options = {}) {
  const limit = options.limit != null ? options.limit : 500;
  const maxPages = options.maxPages != null ? options.maxPages : 200;
  const allRows = [];

  for (let page = 1; page <= maxPages; page += 1) {
    const { data } = await fetchPriceHistoryAggregates(filters, {
      page,
      limit,
    });

    const batch = data || [];
    allRows.push(...batch);

    if (!batch.length || batch.length < limit) {
      break;
    }
  }

  return allRows;
}

const PRICE_HISTORY_CSV_EXPORT_FIELDS = [
  "id",
  "record_at",
  "product_id",
  "product_name",
  "province",
  "retailer",
  "provider",
  "unit_price",
  "unit_quantity",
  "raw_unit_quantity",
];

function buildPriceHistoryRecordsQueryParams(
  filters,
  { page = 1, limit = 500, sort = "-record_at,province,retailer,product_id" } = {}
) {
  const params = new URLSearchParams();
  params.set("access_token", accessToken());
  PRICE_HISTORY_CSV_EXPORT_FIELDS.forEach((field) => params.append("fields[]", field));
  params.set("limit", String(limit));
  params.set("page", String(page));
  params.set("sort", sort);
  applyPriceHistoryQualityFilters(params, filters);
  applyPriceHistoryDimensionFilters(params, filters, { includeDate: true });
  return params;
}

/**
 * One page of filtered `price_history` rows (no grouping), for CSV detail export.
 *
 * @param {Parameters<typeof fetchAllPriceHistoryAggregates>[0]} filters
 * @param {{ page?: number, limit?: number, sort?: string }} [pagination]
 * @returns {Promise<{ data: any[] }>}
 */
export async function fetchPriceHistoryRecords(filters, pagination = {}) {
  const limit = pagination.limit != null ? pagination.limit : 500;
  const page = pagination.page != null ? pagination.page : 1;
  const sort =
    pagination.sort != null ? pagination.sort : "-record_at,province,retailer,product_id";
  const params = buildPriceHistoryRecordsQueryParams(filters, { page, limit, sort });
  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  return { data: json.data || [] };
}

/**
 * Fetch all filtered `price_history` rows by paging until a short page is returned.
 *
 * @param {Parameters<typeof fetchAllPriceHistoryAggregates>[0]} filters
 * @param {{ limit?: number, maxPages?: number }} [options]
 * @returns {Promise<any[]>}
 */
export async function fetchAllPriceHistoryRecords(filters, options = {}) {
  const limit = options.limit != null ? options.limit : 500;
  const maxPages = options.maxPages != null ? options.maxPages : 500;
  const allRows = [];

  for (let page = 1; page <= maxPages; page += 1) {
    const { data } = await fetchPriceHistoryRecords(filters, { page, limit });
    const batch = data || [];
    allRows.push(...batch);
    if (!batch.length || batch.length < limit) {
      break;
    }
  }

  return allRows;
}

/**
 * Fetch raw `price_history` rows for one aggregate-group cell.
 *
 * @param {{
 *  dateFrom?: string,
 *  dateTo?: string,
 *  province?: string,
 *  retailer?: string,
 *  productId?: string,
 *  providersInclude?: string[],
 *  providersExclude?: string[],
 *  unitType?: string
 * }} filters
 * @param {{ year?: number, month?: number, province?: string, retailer?: string, productId?: string, provider?: string, unitType?: string, unit?: string }} breakdown
 * @returns {Promise<any[]>}
 */
export async function fetchPriceHistoryBreakdown(filters, breakdown) {
  const params = new URLSearchParams();
  params.set("access_token", accessToken());
  params.set("limit", "-1");
  params.set("sort", "-record_at,-date_created");
  params.append("fields[]", "id");
  params.append("fields[]", "record_at");
  params.append("fields[]", "date_created");
  params.append("fields[]", "product_id");
  params.append("fields[]", "product_name");
  params.append("fields[]", "raw_product_name");
  params.append("fields[]", "price");
  params.append("fields[]", "unit_price");
  params.append("fields[]", "unit");
  params.append("fields[]", "unit_quantity");
  params.append("fields[]", "retailer");
  params.append("fields[]", "retailer_ref_id");
  params.append("fields[]", "province");
  params.append("fields[]", "provider");
  params.append("fields[]", "human_verified");
  params.append("fields[]", "images");
  params.append("fields[]", "revision_status");
  params.append("fields[]", "revision_metadata");
  params.append("fields[]", PRICE_HISTORY_BREAKDOWN_GPS_PRESENT_JSON_FIELD);
  params.append("fields[]", PRICE_HISTORY_BREAKDOWN_RAW_GPS_JSON_FIELD);

  applyPriceHistoryBreakdownSliceFilters(params, filters, breakdown);

  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  return (json.data || []).map(normalizePriceHistoryBreakdownRow);
}

function normalizeProductIdFromBreakdownRow(row) {
  const raw = row?.product_id;
  if (raw == null) return "";
  if (typeof raw === "object" && raw.id != null) return String(raw.id);
  return String(raw);
}

export function validatePriceHistoryRecordFormValues(formValues) {
  const recordAt = typeof formValues?.recordAt === "string" ? formValues.recordAt.trim() : "";
  const productId = typeof formValues?.productId === "string" ? formValues.productId.trim() : "";
  const productName =
    typeof formValues?.productName === "string" ? formValues.productName.trim() : "";
  const retailer = typeof formValues?.retailer === "string" ? formValues.retailer.trim() : "";
  const retailerRefId =
    typeof formValues?.retailerRefId === "string" ? formValues.retailerRefId.trim() : "";
  const province = typeof formValues?.province === "string" ? formValues.province.trim() : "";
  const unit = typeof formValues?.unit === "string" ? formValues.unit.trim() : "";
  const price = Number(formValues?.price);
  const unitPrice = Number(formValues?.unitPrice);
  const unitQuantityRaw = formValues?.unitQuantity;
  const unitQuantity =
    unitQuantityRaw === null || unitQuantityRaw === undefined || unitQuantityRaw === ""
      ? null
      : Number(unitQuantityRaw);

  if (!recordAt) {
    throw new Error("Date is required.");
  }
  if (!productId) {
    throw new Error("Product is required.");
  }
  if (!retailer) {
    throw new Error("Retailer is required.");
  }
  if (!province) {
    throw new Error("Province is required.");
  }
  if (!unit) {
    throw new Error("Unit is required.");
  }
  if (!Number.isFinite(price) || price <= 0) {
    throw new Error("Price must be greater than zero.");
  }
  if (!Number.isFinite(unitPrice) || unitPrice <= 0) {
    throw new Error("Unit price must be greater than zero.");
  }
  if (unitQuantity != null && (!Number.isFinite(unitQuantity) || unitQuantity <= 0)) {
    throw new Error("Unit quantity must be greater than zero when provided.");
  }

  return {
    recordAt,
    productId,
    productName,
    retailer,
    retailerRefId,
    province,
    price,
    unitPrice,
    unit,
    unitQuantity,
  };
}

function buildPriceHistoryRecordPatchBody(normalizedValues) {
  return {
    record_at: normalizedValues.recordAt,
    product_id: normalizedValues.productId,
    product_name: normalizedValues.productName || undefined,
    retailer: normalizedValues.retailer,
    retailer_ref_id: normalizedValues.retailerRefId || null,
    province: normalizedValues.province,
    price: normalizedValues.price,
    unit_price: normalizedValues.unitPrice,
    unit: normalizedValues.unit,
    unit_quantity: normalizedValues.unitQuantity,
    is_lock: true,
    human_verified: true,
  };
}

/**
 * Dev-only preview of the Beef AI PATCH used when saving a price history record.
 *
 * @param {string} recordId
 * @param {Parameters<typeof validatePriceHistoryRecordFormValues>[0]} formValues
 */
export function getPriceHistoryRecordUpdateRequestPreview(recordId, formValues) {
  if (!recordId) {
    return null;
  }

  const url = `${BEEF_AI_BASE_URL}/items/price_history/${recordId}`;

  try {
    const normalizedValues = validatePriceHistoryRecordFormValues(formValues);
    return {
      method: "PATCH",
      url,
      payload: buildPriceHistoryRecordPatchBody(normalizedValues),
    };
  } catch (error) {
    return {
      method: "PATCH",
      url,
      payload: {},
      blockedReason:
        error?.message || "Fix validation errors before this request can be sent.",
    };
  }
}

/**
 * @param {string} recordId
 * @param {{
 *   recordAt: string,
 *   productId: string,
 *   productName: string,
 *   retailer: string,
 *   retailerRefId?: string,
 *   province: string,
 *   price: number,
 *   unitPrice: number,
 *   unit: string,
 *   unitQuantity?: number | null,
 * }} formValues
 */
export async function updatePriceHistoryRecord(recordId, formValues) {
  if (!recordId) {
    throw new Error("Price history record id is required.");
  }

  const normalizedValues = validatePriceHistoryRecordFormValues(formValues);
  const payload = buildPriceHistoryRecordPatchBody(normalizedValues);

  const json = await beefAiPatch(`/items/price_history/${recordId}`, payload);
  return normalizePriceHistoryBreakdownRow(json?.data || {});
}

/**
 * @param {string} recordId
 */
export async function voidPriceHistoryRecord(recordId) {
  if (!recordId) {
    throw new Error("Price history record id is required.");
  }

  const json = await beefAiPatch(`/items/price_history/${recordId}`, {
    is_void: true,
    human_verified: true,
  });
  return normalizePriceHistoryBreakdownRow(json?.data || {});
}

/**
 * Mark a `price_history` row as human-verified without changing other fields.
 *
 * @param {string} recordId
 */
export async function acceptPriceHistoryRecord(recordId) {
  if (!recordId) {
    throw new Error("Price history record id is required.");
  }

  const json = await beefAiPatch(`/items/price_history/${recordId}`, {
    human_verified: true,
    is_lock: true,
  });
  return normalizePriceHistoryBreakdownRow(json?.data || {});
}

/**
 * Undo a reject: put the row back in the unverified review queue.
 *
 * @param {string} recordId
 */
export async function restorePriceHistoryRecord(recordId) {
  if (!recordId) {
    throw new Error("Price history record id is required.");
  }

  const json = await beefAiPatch(`/items/price_history/${recordId}`, {
    is_void: false,
    human_verified: false,
    is_lock: false,
  });
  return normalizePriceHistoryBreakdownRow(json?.data || {});
}

export { normalizeProductIdFromBreakdownRow };

/**
 * First analysis image URL for one aggregate group (`images[0]` on newest row in the breakdown slice).
 *
 * @param {Parameters<typeof fetchPriceHistoryBreakdown>[0]} filters
 * @param {Parameters<typeof fetchPriceHistoryBreakdown>[1]} breakdown
 * @returns {Promise<string | null>}
 */
export async function fetchAggregateGroupCoverImage(filters, breakdown) {
  try {
    const params = new URLSearchParams();
    params.set("access_token", accessToken());
    params.set("limit", "1");
    params.set("sort", "-record_at,-date_created");
    params.append("fields[]", "id");
    params.append("fields[]", "images");

    applyPriceHistoryBreakdownSliceFilters(params, filters, breakdown);

    const json = await beefAiGet(`/items/price_history?${params.toString()}`);
    const row = (json.data || [])[0];
    if (!row) return null;
    const images = normalizePriceHistoryImages(row.images);
    return images[0] || null;
  } catch (_) {
    return null;
  }
}

/**
 * Cover URLs for a page of aggregate rows. Failed rows map to `null`.
 *
 * @param {Parameters<typeof fetchPriceHistoryBreakdown>[0]} filters
 * @param {{ drilldown?: Parameters<typeof fetchPriceHistoryBreakdown>[1] }[]} aggregateRows
 * @param {{ concurrency?: number }} [options]
 * @returns {Promise<Map<string, string | null>>}
 */
export async function fetchAggregateGroupCoverImagesForPage(filters, aggregateRows, options = {}) {
  const concurrency = options.concurrency ?? AGGREGATE_COVER_FETCH_CONCURRENCY;
  const rows = aggregateRows || [];
  const coverUrlByRowKey = new Map();

  if (!rows.length) {
    return coverUrlByRowKey;
  }

  const tasks = rows.map((aggregateRow) => async () => {
    const breakdown = aggregateRow?.drilldown;
    const rowKey = buildAggregateGroupRowKey(breakdown);
    const coverUrl = await fetchAggregateGroupCoverImage(filters, breakdown);
    return { rowKey, coverUrl };
  });

  const results = await runTasksWithConcurrency(tasks, concurrency);
  results.forEach(({ rowKey, coverUrl }) => {
    coverUrlByRowKey.set(rowKey, coverUrl);
  });

  return coverUrlByRowKey;
}

/**
 * @param {"province" | "retailer" | "provider" | "product_id"} field
 */
export async function fetchDistinctCounts(field, filters) {
  const params = new URLSearchParams();
  params.set("access_token", accessToken());
  params.set("aggregate[count]", "*");
  params.append("groupBy[]", field);
  params.set("limit", "-1");
  applyPriceHistoryQualityFilters(params, filters);
  applyPriceHistoryDimensionFilters(params, filters, { includeDate: true });

  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  return json.data || [];
}

function filtersHaveSelectedProduct(filters = {}) {
  if (Array.isArray(filters.productIds) && filters.productIds.length > 0) {
    return true;
  }
  return Boolean(filters.productId);
}

/**
 * Product Snapshot: `avg(unit_price)` and row `count` per retailer for one or more products (no calendar bucket).
 *
 * @param {{
 *   productId?: string,
 *   productIds?: string[],
 *   dateFrom?: string,
 *   dateTo?: string,
 *   province?: string,
 *   providersInclude?: string[],
 *   unitType?: string,
 *   unit?: string,
 *   isDiscounted?: boolean,
 * }} filters — `productSnapshotRetailerQueryFilters` from the pricing reports panel
 * @returns {Promise<object[]>}
 */
export async function fetchProductAnalyticsRetailerSnapshot(filters = {}) {
  if (!filtersHaveSelectedProduct(filters)) return [];
  const params = createChartAggregateParams(filters, {
    groupBy: ["retailer"],
    sort: "retailer",
  });
  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  return json.data || [];
}

/**
 * Product Snapshot: `avg(unit_price)` and row `count` per province for one or more products (no calendar bucket).
 *
 * @param {{
 *   productId?: string,
 *   productIds?: string[],
 *   dateFrom?: string,
 *   dateTo?: string,
 *   retailer?: string,
 *   providersInclude?: string[],
 *   unitType?: string,
 *   unit?: string,
 *   isDiscounted?: boolean,
 * }} filters — `productSnapshotProvinceQueryFilters` from the pricing reports panel
 * @returns {Promise<object[]>}
 */
export async function fetchProductAnalyticsAverageUnitPriceByProvince(filters = {}) {
  if (!filtersHaveSelectedProduct(filters)) return [];
  const params = createChartAggregateParams(filters, {
    groupBy: ["province"],
    sort: "province",
  });
  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  return json.data || [];
}

const EMPTY_QUARTERLY_PRODUCT_SUMMARY_STATS = {
  averageNationalUnitPrice: null,
  priceHistoryRowCount: 0,
  lowestProvince: null,
  highestProvince: null,
};

async function fetchPriceHistoryChartAggregateRows(filters, options) {
  const params = createChartAggregateParams(filters, options);
  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  return json.data || [];
}

/**
 * Summary KPI aggregates for the panel's active filters (dates, products, province, retailer, providers, discount).
 *
 * @param {{
 *   dateFrom?: string,
 *   dateTo?: string,
 *   province?: string,
 *   retailer?: string,
 *   productId?: string,
 *   productIds?: string[],
 *   providersInclude?: string[],
 *   unitType?: string,
 *   isDiscounted?: boolean,
 * }} filters — `summaryQueryFilters` from the pricing reports panel
 * @returns {Promise<typeof EMPTY_QUARTERLY_PRODUCT_SUMMARY_STATS>}
 */
export async function fetchQuarterlyProductSummaryStats(filters = {}) {
  const hasRetailerFilter =
    Boolean(filters.retailer) ||
    (Array.isArray(filters.retailers) && filters.retailers.length > 0);
  const shouldFetchProvinceComparison = !filters.province && !hasRetailerFilter;

  const aggregateRequests = [
    fetchPriceHistoryChartAggregateRows(filters, { groupBy: [], sort: "" }),
  ];

  if (shouldFetchProvinceComparison) {
    aggregateRequests.push(
      fetchPriceHistoryChartAggregateRows(filters, {
        groupBy: ["province"],
        sort: "province",
      })
    );
  }

  const [nationalRows, provinceRows = []] = await Promise.all(aggregateRequests);

  const nationalRow = nationalRows[0] ?? {};
  const priceHistoryRowCount = getCountFromBeefAiAggregateRow(nationalRow);

  if (priceHistoryRowCount === 0) {
    return { ...EMPTY_QUARTERLY_PRODUCT_SUMMARY_STATS };
  }

  const nationalAvg = getAvgFromBeefAiAggregateRow(nationalRow);
  const averageNationalUnitPrice =
    Number.isFinite(nationalAvg) ? nationalAvg : null;

  if (!shouldFetchProvinceComparison) {
    return {
      averageNationalUnitPrice,
      priceHistoryRowCount,
      lowestProvince: null,
      highestProvince: null,
    };
  }

  const provinceAggregateRows = (provinceRows || [])
    .map((row) => {
      const code = toProv(row.province);
      if (!code) return null;
      return {
        code,
        displayName: provName(code),
        avgUnitPrice: getAvgFromBeefAiAggregateRow(row),
        priceHistoryRowCount: getCountFromBeefAiAggregateRow(row),
      };
    })
    .filter(Boolean);

  const { lowestProvince, highestProvince } =
    pickLowestAndHighestProvinceByAvgPrice(provinceAggregateRows);

  return {
    averageNationalUnitPrice,
    priceHistoryRowCount,
    lowestProvince,
    highestProvince,
  };
}

export async function fetchMonthlyAverageByProduct(filters = {}) {
  return fetchTimeSeriesAverageByProduct(filters, { granularity: "month" });
}

/**
 * @param {object} filters
 * @param {{ granularity?: 'month' | 'week' }} [options]
 */
export async function fetchTimeSeriesAverageByProduct(filters = {}, options = {}) {
  const { granularity = "month" } = options;
  const timeGroupBy =
    granularity === "week"
      ? ["year(record_at)", "week(record_at)"]
      : ["year(record_at)", "month(record_at)"];
  const groupBy = [...timeGroupBy, "product_id"];
  const params = createChartAggregateParams(filters, {
    groupBy,
    sort: groupBy.join(","),
    includeMinMax: true,
  });
  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  return json.data || [];
}

export async function fetchMonthlyAverageByRetailer(filters = {}) {
  const params = createChartAggregateParams(filters, {
    groupBy: ["year(record_at)", "month(record_at)", "retailer"],
    sort: "year(record_at),month(record_at),retailer",
  });
  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  return json.data || [];
}

export async function fetchMonthlyAverageByProvince(filters = {}) {
  const params = createChartAggregateParams(filters, {
    groupBy: ["year(record_at)", "month(record_at)", "province"],
    sort: "year(record_at),month(record_at),province",
  });
  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  return json.data || [];
}

export async function fetchQuarterlyAverageByProduct(filters = {}, options = {}) {
  const { lookbackYears = 1 } = options;
  const lookbackBounds = lookbackDateBounds(lookbackYears);
  const quarterlyFilters = {
    ...filters,
    dateFrom: lookbackBounds.dateFrom,
    dateTo: lookbackBounds.dateTo,
  };
  const params = createChartAggregateParams(quarterlyFilters, {
    groupBy: ["year(record_at)", "month(record_at)"],
    sort: "year(record_at),month(record_at)",
    includeDate: true,
  });
  const json = await beefAiGet(`/items/price_history?${params.toString()}`);
  return json.data || [];
}

/**
 * Load `id` + `name` for specific products (any status). Used so aggregate rows can show names even when
 * `price_history` references products that are not `published` in the catalog-only fetch.
 *
 * @param {string[]} ids
 * @returns {Promise<{ id: string; name?: string }[]>}
 */
export async function fetchProductsByIds(ids) {
  const unique = [...new Set((ids || []).map(String).filter(Boolean))];
  if (!unique.length) return [];

  const chunkSize = 80;
  const out = [];

  for (let i = 0; i < unique.length; i += chunkSize) {
    const chunk = unique.slice(i, i + chunkSize);
    const params = new URLSearchParams();
    params.set("access_token", accessToken());
    params.set("fields", "id,name");
    params.set("limit", "-1");
    params.set("filter[id][_in]", chunk.join(","));

    const json = await beefAiGet(`/items/product?${params.toString()}`);
    const batch = json.data || [];
    out.push(...batch);
  }

  return out;
}

/** Published + draft (etc.): anything not `archived`, so pricing filters match rows that reference unpublished products. */
export async function fetchPublishedProducts(options = {}) {
  const { withRefIdOnly = false } = options;
  const out = [];
  const pageSize = 500;
  const maxPages = 50;

  for (let page = 1; page <= maxPages; page += 1) {
    const listParams = {
      access_token: accessToken(),
      limit: pageSize,
      page,
      sort: "name",
      fields: withRefIdOnly ? "id,status,name,raw_product_name,ref_id" : "id,status,name,raw_product_name",
      "filter[status][_neq]": "archived",
    };
    if (withRefIdOnly) {
      listParams["filter[ref_id][_nnull]"] = "true";
      listParams["filter[ref_id][_nempty]"] = "true";
    }
    const q = buildListQuery(listParams);
    const json = await beefAiGet(`/items/product?${q}`);
    const batch = json.data || [];
    out.push(...batch);
    if (batch.length < pageSize) break;
  }

  return out;
}

function normalizeMarketFlyerImages(imagesValue) {
  let raw = imagesValue;
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch (_) {
      return [];
    }
  }
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (typeof entry === "string") return { url: entry };
      if (entry && typeof entry === "object" && typeof entry.url === "string") {
        return {
          url: entry.url,
          type: typeof entry.type === "string" ? entry.type : undefined,
        };
      }
      return null;
    })
    .filter(Boolean);
}

function normalizeBox2d(box2dValue) {
  let box = box2dValue;
  if (typeof box === "string") {
    try {
      box = JSON.parse(box);
    } catch (_) {
      return null;
    }
  }
  if (!Array.isArray(box) || box.length !== 4) return null;
  const parsed = box.map((value) => Number(value));
  return parsed.every((value) => Number.isFinite(value)) ? parsed : null;
}

function normalizePixelBox(pixelBoxValue) {
  let box = pixelBoxValue;
  if (typeof box === "string") {
    try {
      box = JSON.parse(box);
    } catch (_) {
      return null;
    }
  }
  if (!box || typeof box !== "object") return null;
  const x = Number(box.x);
  const y = Number(box.y);
  const width = Number(box.width);
  const height = Number(box.height);
  if (![x, y, width, height].every((value) => Number.isFinite(value))) return null;
  return { x, y, width, height };
}

function toFiniteNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function normalizeMarketFlyerRow(row) {
  return {
    ...row,
    unit_price: toFiniteNumber(row?.unit_price),
    sale_amount: toFiniteNumber(row?.sale_amount),
    original_price: toFiniteNumber(row?.original_price),
    images: normalizeMarketFlyerImages(row?.images),
    pixel_box: normalizePixelBox(row?.pixel_box),
    box2d: normalizeBox2d(row?.box2d),
  };
}

/**
 * Strict product-id fetch for market_flyer.
 *
 * @param {string | null | undefined} productId
 * @param {{
 *  dateFrom?: string,
 *  dateTo?: string,
 *  province?: string,
 *  retailer?: string,
 *  provider?: string
 * }} [filters]
 * @returns {Promise<any[]>}
 */
export async function fetchMarketFlyerRowsByProductId(productId, filters = {}) {
  if (!productId) return [];
  const params = new URLSearchParams();
  params.set("access_token", accessToken());
  params.set("limit", "-1");
  params.set("sort", "-advertised_date,-date_created");
  params.set("filter[product_id][_eq]", String(productId));
  params.append("fields[]", "id");
  params.append("fields[]", "date_created");
  params.append("fields[]", "provider");
  params.append("fields[]", "provider_id");
  params.append("fields[]", "product_id");
  params.append("fields[]", "product_name");
  params.append("fields[]", "raw_product_name");
  params.append("fields[]", "retailer");
  params.append("fields[]", "raw_retailer");
  params.append("fields[]", "retailer_ref_id");
  params.append("fields[]", "province");
  params.append("fields[]", "advertised_date");
  params.append("fields[]", "unit_price");
  params.append("fields[]", "pricing_unit");
  params.append("fields[]", "is_discounted");
  params.append("fields[]", "sale_type");
  params.append("fields[]", "sale_amount");
  params.append("fields[]", "original_price");
  params.append("fields[]", "detection_index");
  params.append("fields[]", "detection_label");
  params.append("fields[]", "box2d");
  params.append("fields[]", "pixel_box");
  params.append("fields[]", "images");
  params.append("fields[]", "vision_artifacts");
  params.append("fields[]", "metadata");

  if (filters.dateFrom) params.set("filter[advertised_date][_gte]", filters.dateFrom);
  if (filters.dateTo) params.set("filter[advertised_date][_lte]", filters.dateTo);
  if (filters.province) params.set("filter[province][_eq]", filters.province);
  if (filters.retailer) params.set("filter[retailer][_eq]", filters.retailer);
  if (filters.provider) params.set("filter[provider][_eq]", filters.provider);

  const json = await beefAiGet(`/items/market_flyer?${params.toString()}`);
  return (json.data || []).map(normalizeMarketFlyerRow);
}

export function summarizeMarketFlyerRows(rows = []) {
  const provinceCountByCode = new Map();
  const retailerCountByName = new Map();
  const providerCountByName = new Map();

  for (const row of rows) {
    if (row?.province) {
      provinceCountByCode.set(row.province, (provinceCountByCode.get(row.province) || 0) + 1);
    }
    if (row?.retailer) {
      retailerCountByName.set(row.retailer, (retailerCountByName.get(row.retailer) || 0) + 1);
    }
    if (row?.provider) {
      providerCountByName.set(row.provider, (providerCountByName.get(row.provider) || 0) + 1);
    }
  }

  const toSortedArray = (map, keyName) =>
    [...map.entries()]
      .map(([key, count]) => ({ [keyName]: key, count }))
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return String(a[keyName]).localeCompare(String(b[keyName]));
      });

  return {
    totalPromotions: rows.length,
    distinctProvinceCount: provinceCountByCode.size,
    distinctRetailerCount: retailerCountByName.size,
    distinctProviderCount: providerCountByName.size,
    provinces: toSortedArray(provinceCountByCode, "province"),
    retailers: toSortedArray(retailerCountByName, "retailer"),
    providers: toSortedArray(providerCountByName, "provider"),
  };
}

export async function fetchMarketFlyerPromotionSummary(productId, filters = {}) {
  const rows = await fetchMarketFlyerRowsByProductId(productId, filters);
  return summarizeMarketFlyerRows(rows);
}

export function mapMarketFlyerRowsToDisplayItems(rows) {
  return (rows || []).map((row, index) => {
    const imageUrl = row?.images?.[0]?.url || "";
    return {
      id: row?.id || `market-flyer-${index}`,
      advertisedDate: row?.advertised_date || "—",
      product: row?.product_name || row?.raw_product_name || "—",
      retailer: row?.retailer || row?.raw_retailer || "—",
      province: row?.province || "—",
      provider: row?.provider || "—",
      providerId: row?.provider_id || null,
      unitPriceValue: toFiniteNumber(row?.unit_price),
      unitPrice:
        toFiniteNumber(row?.unit_price) != null
          ? `${toFiniteNumber(row?.unit_price)}${row?.pricing_unit ? ` per ${row.pricing_unit}` : ""}`
          : "—",
      pricingUnit: row?.pricing_unit || "—",
      isDiscounted: Boolean(row?.is_discounted),
      saleType: row?.sale_type || null,
      saleAmount: toFiniteNumber(row?.sale_amount),
      originalPrice: toFiniteNumber(row?.original_price),
      detectionIndex: typeof row?.detection_index === "number" ? row.detection_index : null,
      detectionLabel: row?.detection_label || "—",
      imageUrl,
      images: row?.images || [],
      pixelBox: row?.pixel_box || null,
      box2d: row?.box2d || null,
      raw: row,
    };
  });
}

function normalizeProductNameForMatch(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export async function resolvePriceProductByCandidateNames(candidateNames = []) {
  const candidates = [...new Set((candidateNames || []).map((name) => String(name || "").trim()).filter(Boolean))];
  if (!candidates.length) return null;

  const normalizedCandidates = new Set(candidates.map((name) => normalizeProductNameForMatch(name)).filter(Boolean));
  if (!normalizedCandidates.size) return null;

  const products = await fetchPublishedProducts();
  const matches = (products || []).filter((product) => {
    const normalizedProductName = normalizeProductNameForMatch(product?.name);
    const normalizedRawProductName = normalizeProductNameForMatch(product?.raw_product_name);
    return normalizedCandidates.has(normalizedProductName) || normalizedCandidates.has(normalizedRawProductName);
  });

  if (!matches.length) return null;

  const exactCaseInsensitive = matches.find((product) =>
    candidates.some((candidate) => String(candidate).toLowerCase() === String(product?.name || "").toLowerCase())
  );
  const winner =
    exactCaseInsensitive ||
    matches[0];

  return winner
    ? {
        id: String(winner.id),
        name: winner.name || winner.raw_product_name || String(winner.id),
        raw_product_name: winner.raw_product_name || null,
      }
    : null;
}
